import React, { FunctionComponent, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import idx from 'idx';
import { captureApolloError } from 'util/apolloError';
import {
  ReviewCrsImportStatusQuery,
  ReviewCrsImportStatusQueryVariables,
  InitiateCrsImportMutation,
  InitiateCrsImportMutationVariables,
} from 'types/graphql';
import { ID, matchType } from 'util/localTypes';
import Box from 'system/base/Box';
import Notice from 'system/elements/Notice';
import Panel from 'system/elements/Panel';
import LoadingSpinner from 'system/elements/Spinner';
import { PrimaryButton } from 'system/base/Button';
import Text from 'system/base/Text';

type Props = {
  reviewId: ID;
};

const QUERY = gql`
  query ReviewCrsImportStatus($reviewId: ID!) {
    review: node(id: $reviewId) {
      ... on Review {
        cochraneReviewLink {
          id
          crsPendingStudiesCount
        }
      }
    }
  }
`;

const INITIATE_MUTATION = gql`
  mutation initiateCrsImport($cochraneId: ID!) {
    initiateCrsImport(input: { cochraneReviewLinkId: $cochraneId }) {
      success
    }
  }
`;

const ImportCRS: FunctionComponent<Props> = ({ reviewId }: Props) => {
  const { data, error, loading } = useQuery<
    ReviewCrsImportStatusQuery,
    ReviewCrsImportStatusQueryVariables
  >(QUERY, { variables: { reviewId } });

  const [
    initiateImport,
    { data: initiateData, error: initiateError, loading: initiateLoading },
  ] = useMutation<
    InitiateCrsImportMutation,
    InitiateCrsImportMutationVariables
  >(INITIATE_MUTATION);

  useEffect(() => {
    if (idx(initiateData, (_) => _.initiateCrsImport.success)) {
      window.location.href = `/reviews/${reviewId}`;
    }
  }, [initiateData, reviewId]);

  const review = matchType(data?.review, 'Review');
  const pendingCount = review?.cochraneReviewLink?.crsPendingStudiesCount;

  if (loading) {
    return (
      <Panel primary>
        <LoadingSpinner />
      </Panel>
    );
  }

  if (error) {
    captureApolloError(error);
    return (
      <Panel primary>
        <Notice type="warning">{error.message}</Notice>
      </Panel>
    );
  }

  if (initiateError) captureApolloError(initiateError); // error message is passed to the Panel

  return (
    <Panel primary>
      {initiateError && <Notice type="error">{initiateError.message}</Notice>}
      <p>
        There are{' '}
        <strong>
          {pendingCount === 0 || pendingCount == null ? 'no' : pendingCount}{' '}
          studies
        </strong>{' '}
        in the <strong>Cochrane Registry of Studies</strong> for this review
        that are ready to be screened.
      </p>
      {pendingCount ? (
        <>
          <Box marginTop={3}>
            {initiateLoading ? (
              <PrimaryButton>
                <LoadingSpinner />
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={() =>
                  initiateImport({
                    variables: {
                      cochraneId: idx(review, (_) => _.cochraneReviewLink.id)!,
                    },
                  })
                }
              >
                Import {pendingCount} studies from CRS
              </PrimaryButton>
            )}
          </Box>
          <Box marginTop={3}>
            <Text textStyle="muted">
              Studies will automatically be imported into title and abstract
              screening
            </Text>
          </Box>
        </>
      ) : (
        <Box marginTop={3}>
          Speak with your Cochrane Information Specialist about importing
          studies directly from CRS.
        </Box>
      )}
    </Panel>
  );
};

export default ImportCRS;
